import { supabase } from './supabase'
import type { SupportStarLevel } from './hubCopy'

/**
 * Stars for a platform support ticket — 172 (David, 11 Oct).
 *
 * Four levels: none (the default), thanks, important, critical. The AMOUNTS
 * come from the server (`levels`, derived from the `support.ticket_thanks`
 * rate on #/admin/stars: a tenth, half, the whole), never from this file, so
 * a changed rate cannot leave the buttons quoting yesterday's numbers.
 *
 * Raise only: a ticket goes up a level later and pays the difference; it never
 * goes down. Platform lane holders only, never on their own ticket — the
 * server decides (`can_award`); this file draws what it was told.
 */

export type { SupportStarLevel }

export const STAR_LEVEL_ORDER: readonly SupportStarLevel[] = ['thanks', 'important', 'critical']

/** David's words for the three levels, for the staff picker (staff screens are English, SHL-25). */
export const STAFF_LEVEL_LABELS: Record<SupportStarLevel, string> = {
  thanks: 'Good idea · bug report — thanks',
  important: 'Good idea · important bug report',
  critical: 'Great idea · critical bug report',
}

export type SupportStars = {
  level: SupportStarLevel | null
  /** Everything paid on this ticket, all steps. */
  starsPaid: number
  /** Everything offered; more than paid only when a monthly cap bit. */
  starsOffered: number
  awardedAt: string | null
  /** Staff only; null for the asker. */
  awardedBy: string | null
  canAward: boolean
  /** Staff only: each level's amount at today's rate. */
  levels: Record<SupportStarLevel, number> | null
}

export type AwardResult = { level: SupportStarLevel; offered: number; paid: number; outcome: string }

export async function loadSupportStars(questionId: string): Promise<SupportStars> {
  const { data, error } = await supabase.rpc('support_star_award', { p_content_id: questionId })
  if (error) throw new Error(error.message)
  const r = (data ?? [])[0] ?? {}
  return {
    level: (r.level as SupportStarLevel | null) ?? null,
    starsPaid: r.stars_paid ?? 0,
    starsOffered: r.stars_offered ?? 0,
    awardedAt: r.awarded_at ?? null,
    awardedBy: r.awarded_by ?? null,
    canAward: !!r.can_award,
    levels: r.levels ?? null,
  }
}

export async function awardSupportStars(questionId: string, level: SupportStarLevel): Promise<AwardResult> {
  const { data, error } = await supabase.rpc('award_support_stars', { p_content_id: questionId, p_level: level })
  if (error) throw new Error(error.message)
  const r = (data ?? [])[0] ?? {}
  return { level: r.level, offered: r.stars_offered ?? 0, paid: r.stars_paid ?? 0, outcome: r.outcome ?? 'nothing' }
}

/** The levels a ticket can still go up to. */
export function levelsAbove(current: SupportStarLevel | null): SupportStarLevel[] {
  const at = current ? STAR_LEVEL_ORDER.indexOf(current) : -1
  return STAR_LEVEL_ORDER.slice(at + 1)
}

/** What pressing a level would pay now: the difference from the current level's amount. */
export function starsForStep(levels: Record<SupportStarLevel, number>, current: SupportStarLevel | null, to: SupportStarLevel): number {
  return Math.max(levels[to] - (current ? levels[current] : 0), 0)
}

/** The staff sentence for where a ticket stands — one home (rule 11). */
export function starStateWords(s: SupportStars): string {
  if (!s.level) return 'No stars yet. Most tickets get none; that is the default.'
  const who = s.awardedBy ? ` by ${s.awardedBy}` : ''
  const when = s.awardedAt ? ` · ${new Date(s.awardedAt).toLocaleDateString('en-CA')}` : ''
  const paid =
    s.starsPaid < s.starsOffered
      ? `${s.starsPaid.toLocaleString()} of ${s.starsOffered.toLocaleString()} stars paid — the person had reached this month's limit.`
      : `${s.starsPaid.toLocaleString()} stars paid.`
  return `${STAFF_LEVEL_LABELS[s.level]}${who}${when}. ${paid}`
}
